/**
 * Pure helpers for project ownership / notification routing.
 * Extracted so the agency vs developer logic can be tested without
 * spinning up the full ManageProject / ProjectApprovals components.
 */

export type ProjectMode = 'developer' | 'agency' | null;

export interface OwnerInputs {
  mode: ProjectMode;
  devProfileId?: string | null;
  myAgencyId?: string | null;
  marketingAgencyId?: string | null;
  linkedDeveloperId?: string | null;
}

export interface ResolvedOwners {
  developer_id: string | null;
  marketing_agency_id: string | null;
}

/**
 * Resolve the (developer_id, marketing_agency_id) pair for a project insert
 * based on whether the submitter is acting as a developer or as an agency.
 */
export function resolveProjectOwners(input: OwnerInputs): ResolvedOwners {
  const { mode, devProfileId, myAgencyId, marketingAgencyId, linkedDeveloperId } = input;
  return {
    developer_id: mode === 'developer' ? (devProfileId ?? null) : (linkedDeveloperId || null),
    marketing_agency_id: mode === 'agency' ? (myAgencyId ?? null) : (marketingAgencyId || null),
  };
}

/**
 * Given a project record (with optional joined developer_profiles + agencies),
 * pick the user_id that should receive QC review notifications.
 *
 * Priority:
 *   1) developer_profiles.user_id (the developer that submitted it)
 *   2) marketing_agency.created_by (the agency that submitted it)
 *   3) null (caller should skip notifying)
 */
export interface ProjectForNotify {
  developer_id?: string | null;
  developer_profiles?: { user_id?: string | null } | null;
  marketing_agency_id?: string | null;
  marketing_agency?: { created_by?: string | null } | null;
}

export function resolveProjectSubmitterUserId(project: ProjectForNotify | null | undefined): string | null {
  if (!project) return null;
  if (project.developer_profiles?.user_id) return project.developer_profiles.user_id;
  if (project.marketing_agency?.created_by) return project.marketing_agency.created_by;
  return null;
}
